import { useEffect } from 'react';
import dayjs from 'dayjs';
import { Link, useParams } from 'react-router-dom';

import {
  currencyFormat,
  minifyString as minify,
  minifyString
} from '@dor/utils';
import { Table } from '@/common/components/Table/component.tsx';
import { PageTitle } from '@/common/components/PageTitle/component.tsx';
import { ViewLayout } from '@/common/components/ViewLayout/component.tsx';
import { InfoCard } from '@/common/components/InfoCard/component.tsx';
import { useDevicesProvider } from '@/common/providers/DevicesProvider/provider.tsx';
import { useRewardsProvider } from '@/common/providers/RewardsProvider/provider.tsx';
import { CopyableLinkableContent } from '@/common/components/CopyableLinkableContent/component.tsx';
import { DeploymentDAGExplorerBaseUrl } from '@/common/consts/api.ts';

import styles from './view.module.scss';

export const DeviceDetailView = () => {
  const { deviceId } = useParams();

  const devices = useDevicesProvider();
  const rewards = useRewardsProvider();

  useEffect(() => {
    deviceId && devices.device.fetch({ deviceId }, {});
    deviceId &&
      rewards.rewards.fetch({}, { pubKey: deviceId }, { pageSize: 10 });
    devices.checkins.fetch({}, { pubKey: deviceId }, { pageSize: 10 });
  }, [deviceId]);

  return (
    <ViewLayout>
      <PageTitle>Device Details</PageTitle>
      <InfoCard
        records={[
          {
            label: 'Device ID',
            value: (
              <CopyableLinkableContent
                value={
                  devices.device.resource.resource?.pubKey ?? deviceId ?? ''
                }
                content={(v) => minify(v, 12, 12)}
              />
            )
          },
          {
            label: 'Rewards Address',
            value: (
              <CopyableLinkableContent
                value={devices.device.resource.resource?.rewardsAddress ?? ''}
                linkTo={(v) => `${DeploymentDAGExplorerBaseUrl}/address/${v}`}
                isExternal
                content={(v) => minify(v, 12, 12)}
              />
            )
          },
          {
            label: 'Last Check-In',
            value: dayjs
              .unix(
                Math.max(
                  ...devices.checkins.resource.resource.map(
                    (checkin) => checkin.dts
                  )
                )
              )
              .fromNow()
          }
        ]}
      />
      <div className={styles.information}>
        <Table
          title="Device Check-ins"
          columns={[
            { key: 'deviceId', title: 'device id' },
            { key: 'timestamp', title: 'timestamp' },
            { key: 'dataHash', title: 'Data Hash' },
            {
              key: 'ordinal',
              title: 'ordinal',
              style: 'link'
            }
          ]}
          data={devices.checkins.resource.resource.map((checkin) => ({
            timestamp: (
              <span title={dayjs.unix(checkin.dts).toISOString()}>
                {dayjs.unix(checkin.dts).fromNow()}
              </span>
            ),
            deviceId: minify(checkin.proof.id),
            dataHash: minify(checkin.checkInHash),
            ordinal: (
              <Link to={`/snapshots/${checkin.ordinal}`}>
                {checkin.ordinal}
              </Link>
            )
          }))}
          pagination={{
            currentPage: devices.checkins.pagination.currentPage,
            pageSize: devices.checkins.pagination.pageSize,
            totalPages: devices.checkins.pagination.totalPages,
            totalItems: devices.checkins.pagination.totalItems,
            onPageChange: (pageNumber) => devices.checkins.goToPage(pageNumber)
          }}
        />
        <Table
          title="DOR Commissions"
          columns={[
            { key: 'toAddress', title: 'to', style: 'link' },
            { key: 'ordinal', title: 'Ordinal', style: 'link' },
            { key: 'amount', title: 'amount' }
          ]}
          data={rewards.rewards.resource.resource.map((reward) => ({
            toAddress: (
              <a
                href={`${DeploymentDAGExplorerBaseUrl}/address/${reward.address}`}
                target="_blank"
              >
                {minifyString(reward.address, 4, 4)}
              </a>
            ),
            ordinal: (
              <Link to={`/snapshots/${reward.snapshotOrdinal}`}>
                {reward.snapshotOrdinal}
              </Link>
            ),
            amount: currencyFormat('DOR', reward.amount, 'DECIMAL', 8)
          }))}
          pagination={{
            currentPage: rewards.rewards.pagination.currentPage,
            pageSize: rewards.rewards.pagination.pageSize,
            totalPages: rewards.rewards.pagination.totalPages,
            totalItems: rewards.rewards.pagination.totalItems,
            onPageChange: (pageNumber) => rewards.rewards.goToPage(pageNumber)
          }}
        />
      </div>
    </ViewLayout>
  );
};
